import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { BarChart3 } from 'lucide-react';
import { DailyMetalData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';

interface MetalAverageTableProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  processes: string[];
  title: string;
}

const MetalAverageTable: React.FC<MetalAverageTableProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  processes,
  title 
}) => {
  // Process configuration with Vietnamese names and colors
  const processConfig = {
    phoi: { name: 'Phôi', color: 'bg-amber-100' },
    han: { name: 'Hàn', color: 'bg-red-100' },
    son: { name: 'Sơn', color: 'bg-blue-100' },
    kiemTran: { name: 'Kiểm trần', color: 'bg-violet-100' },
    kiemDan: { name: 'Kiểm đan', color: 'bg-emerald-100' },
    thanhPham: { name: 'Thành phẩm', color: 'bg-orange-100' },
  };

  // Calculate average Kg for each process in the selected date range
  const averageStats = useMemo(() => {
    if (!dailyData.length) return {};

    const stats: Record<string, number> = {};

    processes.forEach(processKey => {
      // Get all Kg values for this process (excluding zero days)
      const values = dailyData
        .map(day => day.processes[processKey as keyof typeof day.processes]?.weight || 0)
        .filter(value => value > 0);

      // Calculate average
      const average = values.length > 0 
        ? values.reduce((sum, value) => sum + value, 0) / values.length 
        : 0;

      stats[processKey] = average;
    });

    return stats;
  }, [dailyData, processes]);

  // Format numbers
  // Use Vietnamese locale formatting for numbers

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ) : Object.keys(averageStats).length > 0 ? (
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center border-r font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2 min-w-[120px] sm:min-w-[200px]">Chỉ tiêu</TableHead>
                  <TableHead className="text-center font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2 min-w-[100px] sm:min-w-[150px]">Trung bình Kg</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processes.map((processKey, index) => {
                  const config = processConfig[processKey as keyof typeof processConfig];
                  const average = averageStats[processKey] || 0;
                  
                  return (
                    <TableRow key={processKey} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <TableCell className={`font-medium text-center border-r text-xs sm:text-sm px-2 sm:px-4 py-2 ${config?.color || 'bg-gray-100'}`}>
                        {config?.name || processKey}
                      </TableCell>
                      <TableCell className="text-center font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2">
                        {formatNumberDisplay(average, 2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Không có dữ liệu trung bình</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalAverageTable;